import { ListPayload } from 'core/http/response';
import { DeclarationCountry } from '../declarations/enums/common/declaration-country';
import { DeclarationInternalType } from '../declarations/enums/common/declaration-internal-type';
import { AISMessageTypes, ENSMessageTypes, MessageTypes } from '../declarations/enums/common/declaration-types';
import {
    transformGeneralizedListOfProductTemplatesForClient,
    transformGeneralizedProductTemplateForClient,
    transformGeneralizedProductTemplateForServer,
} from '../../utils/productTemplateActionUtils';
import * as client from './client';
import { ListProductTemplateParams } from './client';
import { ProductTemplate } from './products';

export interface ProductTemplateClient {
    list: (params?: Partial<ListProductTemplateParams>) => Promise<ListPayload<ProductTemplate>>;
    save: (product: ProductTemplate) => Promise<ProductTemplate | undefined>;
    update: (product: ProductTemplate) => Promise<ProductTemplate | undefined>;
    get: (productId: string) => Promise<ProductTemplate | undefined>;
    remove: (productId: string) => Promise<unknown>;
    listTags: () => Promise<string[]>;
}

type Variant = 'CDS_EXPORT' | 'CDS_IMPORT' | 'UK_NCTS' | 'AES' | 'NCTS' | 'H1' | 'H7' | 'ENS';

const emptyList: ListPayload<ProductTemplate> = { list: [], pageNumber: 0, pageSize: 0, total: 0 };

const getVariant = (
    country: DeclarationCountry,
    internalType: DeclarationInternalType,
    messageType: MessageTypes
): Variant | undefined => {
    if (country === DeclarationCountry.UK) {
        if (internalType === DeclarationInternalType.EXPORT) return 'CDS_EXPORT';
        if (internalType === DeclarationInternalType.NCTS) return 'UK_NCTS';
        return 'CDS_IMPORT';
    }

    if (internalType === DeclarationInternalType.EXPORT) return 'AES';
    if (internalType === DeclarationInternalType.NCTS) return 'NCTS';
    if (messageType === AISMessageTypes.H7) return 'H7';
    if (messageType === ENSMessageTypes.ENS) return 'ENS';
    if (messageType === AISMessageTypes.H1) return 'H1';
    return undefined;
};

// The generalized endpoints serve every product template kind that stores its fields under `template`.
const usesGeneralizedEndpoint = (country: DeclarationCountry, internalType: DeclarationInternalType) =>
    country === DeclarationCountry.UK ||
    (country === DeclarationCountry.IRELAND &&
        (internalType === DeclarationInternalType.EXPORT || internalType === DeclarationInternalType.NCTS)) ||
    internalType === DeclarationInternalType.ENS;

const generalizedList =
    (
        request: (params: ListProductTemplateParams) => Promise<ListPayload<any>>,
        withMessageType: boolean,
        messageType: MessageTypes
    ) =>
    async (params?: Partial<ListProductTemplateParams>) => {
        const requestParams = withMessageType ? { ...params, messageType } : params;
        return transformGeneralizedListOfProductTemplatesForClient(await request(requestParams as any));
    };

const generalizedSave =
    (request: (product: ProductTemplate) => Promise<any>, declarationName?: MessageTypes) =>
    async (product: ProductTemplate) => {
        const productToSend = declarationName ? { ...product, declarationName } : product;
        return transformGeneralizedProductTemplateForClient(
            await request(transformGeneralizedProductTemplateForServer(productToSend))
        );
    };

const noOpClient: ProductTemplateClient = {
    list: async () => emptyList,
    save: async () => undefined,
    update: async () => undefined,
    get: async () => undefined,
    remove: async () => undefined,
    listTags: async () => [],
};

const createVariantClient = (variant: Variant, messageType: MessageTypes) => {
    switch (variant) {
        case 'CDS_EXPORT':
            return {
                list: generalizedList(client.listCdsExportProducts, true, messageType),
                save: generalizedSave(client.createCdsExportProductTemplate, messageType),
                update: generalizedSave(client.editCdsExportProductTemplate, messageType),
                listTags: () => client.listCdsExportTags(messageType),
            };
        case 'CDS_IMPORT':
            return {
                list: generalizedList(client.listCdsImportProductsTemplates, true, messageType),
                save: generalizedSave(client.createCdsImportProductTemplate, messageType),
                update: generalizedSave(client.editCdsImportProductTemplate, messageType),
                listTags: () => client.listCdsImportTags(messageType),
            };
        case 'UK_NCTS':
            return {
                list: generalizedList(client.listUkNctsProductTemplates, true, messageType),
                save: generalizedSave(client.createUkNctsProductTemplate, messageType),
                update: generalizedSave(client.editUkNctsProductTemplate, messageType),
                listTags: () => client.listUkNctsTags(messageType),
            };
        case 'AES':
            return {
                list: generalizedList(client.listAesProductTemplates, true, messageType),
                save: generalizedSave(client.createAesProductTemplate, messageType),
                update: generalizedSave(client.editAesProductTemplate, messageType),
                listTags: () => client.listAesTags(messageType),
            };
        case 'NCTS':
            return {
                list: generalizedList(client.listNctsProductTemplates, true, messageType),
                save: generalizedSave(client.createNctsProductTemplate, messageType),
                update: generalizedSave(client.editNctsProductTemplate, messageType),
                listTags: () => client.listNctsTags(messageType),
            };
        case 'ENS':
            return {
                list: generalizedList(client.listEnsProducts, false, messageType),
                save: generalizedSave(client.createEnsProductTemplate),
                update: generalizedSave(client.editEnsProductTemplate),
                listTags: () => client.listEnsTags(),
            };
        case 'H7':
            return {
                list: client.listH7Products,
                save: client.createH7ProductTemplate,
                update: client.editH7ProductTemplate,
                listTags: () => client.listH7Tags(),
            };
        case 'H1':
            return {
                list: client.listProducts,
                save: client.createIrelandProductTemplate,
                update: client.editProductTemplate,
                listTags: () => client.listH1Tags(),
            };
    }
};

const createGetAndRemove = (
    country: DeclarationCountry,
    internalType: DeclarationInternalType,
    messageType: MessageTypes
): Pick<ProductTemplateClient, 'get' | 'remove'> => {
    if (usesGeneralizedEndpoint(country, internalType)) {
        return {
            get: async (productId) =>
                transformGeneralizedProductTemplateForClient(await client.getGeneralizedProductTemplate(productId)),
            remove: client.deleteGeneralizedProductTemplate,
        };
    }
    if (messageType === AISMessageTypes.H7) {
        return { get: client.getH7Product, remove: client.deleteH7Product };
    }
    if (messageType === AISMessageTypes.H1) {
        return { get: client.getProduct, remove: client.deleteProduct };
    }
    return { get: noOpClient.get, remove: noOpClient.remove };
};

// Picks the endpoints that serve one kind of product template. A combination without endpoints gets
// methods that resolve to an empty result, which is what the pages relied on before.
export const getProductTemplateClient = (
    country: DeclarationCountry,
    internalType: DeclarationInternalType,
    messageType: MessageTypes
): ProductTemplateClient => {
    const variant = getVariant(country, internalType, messageType);
    const variantClient = variant ? createVariantClient(variant, messageType) : noOpClient;

    return {
        ...noOpClient,
        ...variantClient,
        ...createGetAndRemove(country, internalType, messageType),
    };
};
